import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, isRef as _isRef, withCtx as _withCtx, createSlots as _createSlots, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

import type { Salutation } from '@src/view_models/Salutation';
import type { AddressValidation } from '@src/view_models/Validation';
import { Validity } from '@src/view_models/Validity';
import { computed, onBeforeMount, ref, toRefs } from 'vue';
import type { Country } from '@src/view_models/Country';
import PostalAddressFields from '@src/components/shared/PostalAddressFields.vue';
import NameFields from '@src/components/shared/NameFields.vue';
import EmailField from '@src/components/shared/form_fields/EmailField.vue';
import IncentivesField from '@src/components/shared/form_fields/IncentivesField.vue';
import { useAddressFunctions } from '@src/components/pages/membership_form/AddressFunctions';
import AutofillHandler from '@src/components/shared/AutofillHandler.vue';
import TextField from '@src/components/shared/form_fields/TextField.vue';
import { useReceiptModel } from '@src/components/pages/membership_form/useReceiptModel';
import { useIncentivesModel } from '@src/components/pages/membership_form/useIncentivesModel';
import type { CheckboxFormOption } from '@src/components/shared/form_fields/FormOptions';
import { useI18n } from 'vue-i18n';
import { AddressTypeModel } from '@src/view_models/AddressTypeModel';
import { useStore } from 'vuex';
import CheckboxField from '@src/components/shared/form_fields/CheckboxField.vue';

interface Props {
	validateAddressUrl: string;
	validateEmailUrl: string;
	countries: Country[];
	salutations: Salutation[];
	addressValidationPatterns: AddressValidation;
	dateOfBirthValidationPattern: String;
}

export default /*@__PURE__*/_defineComponent({
  __name: 'Address',
  props: {
    validateAddressUrl: {},
    validateEmailUrl: {},
    countries: {},
    salutations: {},
    addressValidationPatterns: {},
    dateOfBirthValidationPattern: {}
  },
  setup(__props: any) {

const props = __props;
const store = useStore();
const { t } = useI18n();

const { addressValidationPatterns } = toRefs( props );
const countryWasRestored = ref<boolean>( false );
const { receiptNeeded } = useReceiptModel( store, true );

const incentivesModel = useIncentivesModel( store );

const {
	formData,
	fieldErrors,

	initializeDataFromStore,
	onFieldChange,
	onAutofill,
} = useAddressFunctions( {
	addressValidationPatterns: addressValidationPatterns.value,
	dateOfBirthValidationPattern: props.dateOfBirthValidationPattern.toString(),
}, store );

const incentivesAsOptions: CheckboxFormOption[] = [
	{ value: 'tote_bag', label: t( 'membership_form_incentive' ), id: 'tote_bag' },
];

const addressTypeFromStore = computed( (): AddressTypeModel => {
	return store.state.membership_address.addressType;
} );

const isPerson = computed( (): boolean => {
	return store.getters[ 'membership_address/isPerson' ];
} );

onBeforeMount( () => {
	countryWasRestored.value = store.state.membership_address.validity.country === Validity.RESTORED;
	initializeDataFromStore();
} );


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(AutofillHandler, { onAutofill: _unref(onAutofill) }, {
    default: _withCtx(() => [
      _createVNode(NameFields, {
        "show-error": _unref(fieldErrors),
        "form-data": _unref(formData),
        salutations: __props.salutations,
        onFieldChanged: _unref(onFieldChange),
        "address-type": addressTypeFromStore.value
      }, null, 8, ["show-error", "form-data", "salutations", "onFieldChanged", "address-type"]),
      _createVNode(PostalAddressFields, {
        "show-error": _unref(fieldErrors),
        "post-code-validation": _unref(addressValidationPatterns).postcode,
        "form-data": _unref(formData),
        countries: __props.countries,
        "country-was-restored": countryWasRestored.value,
        onFieldChanged: _unref(onFieldChange)
      }, null, 8, ["show-error", "post-code-validation", "form-data", "countries", "country-was-restored", "onFieldChanged"]),
      _createVNode(CheckboxField, {
        modelValue: _unref(receiptNeeded),
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(receiptNeeded) ? (receiptNeeded).value = $event : null)),
        name: "receipt-option",
        "input-id": "receipt-option-person"
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_ctx.$t( 'receipt_needed_membership_page' )), 1)
        ]),
        _: 1
      }, 8, ["modelValue"]),
      _createVNode(IncentivesField, {
        "incentive-form-field-options": incentivesAsOptions,
        modelValue: _unref(incentivesModel),
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(incentivesModel) ? (incentivesModel).value = $event : null)),
        "is-max-width-field": true,
        onFieldChanged: _unref(onFieldChange)
      }, null, 8, ["modelValue", "onFieldChanged"]),
      (isPerson.value)
        ? (_openBlock(), _createBlock(TextField, {
            key: 0,
            name: "date",
            "input-id": "date",
            label: _ctx.$t( 'membership_form_birth_date_label' ),
            modelValue: _unref(formData).date.value,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(formData).date.value) = $event)),
            placeholder: _ctx.$t( 'membership_form_birth_date_placeholder' ),
            "show-error": _unref(fieldErrors).date,
            "error-message": _ctx.$t( 'membership_form_birth_date_error' ),
            "scroll-target-id": "address-form-date-of-birth",
            "is-max-width-field": true,
            onFieldChanged: _unref(onFieldChange)
          }, _createSlots({ _: 2 }, [
            (!_unref(fieldErrors).date)
              ? {
                  name: "message",
                  fn: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t( 'membership_form_birth_date_help_text' )), 1)
                  ]),
                  key: "0"
                }
              : undefined
          ]), 1032, ["label", "modelValue", "placeholder", "show-error", "error-message", "onFieldChanged"]))
        : _createCommentVNode("", true),
      _createVNode(EmailField, {
        "show-error": _unref(fieldErrors).email,
        modelValue: _unref(formData).email.value,
        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(formData).email.value) = $event)),
        "is-max-width-field": true,
        onFieldChanged: _unref(onFieldChange)
      }, null, 8, ["show-error", "modelValue", "onFieldChanged"])
    ]),
    _: 1
  }, 8, ["onAutofill"]))
}
}

})